import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { CxpClientRequestApiService } from '../services/cxp-client-request-api.service';
import { CXP_ROUTE_PARAM_REQUEST_ID } from '../models/routing-constants';
import { ROUTE_TOKEN_CXP_TASKS, ROUTE_TOKEN_MAIN_BASE } from '../../../routing-constants';

/**
 * Route guard for every request-scoped page under `/main/tasks` — `request/:requestId`,
 * `esign/:requestId` and `esign/:requestId/sign`. Each names the request in the same
 * `requestId` param, so the guard reads the id identically whichever path it is on.
 *
 * Fires a targeted single-row Hasura accessibility probe so the decision
 * resolves up front without depending on the legacy `ClientRequestsState`
 * load chain — that's the only reliable way to handle direct URL loads,
 * where the component's own state orchestration hasn't run yet.
 *
 * Decision matrix:
 *   - flag off                          → allow
 *   - accessible (exists, not locked)   → allow (component then loads everything it needs as today)
 *   - not accessible (locked / missing) → UrlTree to /main/tasks
 *   - network / GraphQL error           → UrlTree to /main/tasks
 */
export const cxpRequestLockdownGuard: CanActivateFn = (route) => {
  const idParam = route.paramMap.get(CXP_ROUTE_PARAM_REQUEST_ID);
  const requestId = idParam !== null ? Number(idParam) : NaN;
  if (!Number.isFinite(requestId)) return true;

  const router = inject(Router);
  const launchDarklyService = inject(LaunchDarklyService);
  const api = inject(CxpClientRequestApiService);
  const redirect: UrlTree = router.createUrlTree([ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_CXP_TASKS]);

  return launchDarklyService.getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_REQUEST_LISTS_LOCKDOWN).pipe(
    take(1),
    switchMap<boolean, Observable<boolean | UrlTree>>((flagEnabled) => {
      if (!flagEnabled) return of(true);
      return api.isClientRequestAccessible(requestId).pipe(
        map((accessible) => (accessible ? true : redirect)),
        catchError(() => of<boolean | UrlTree>(redirect)),
      );
    }),
  );
};
